// Ranking engine for the IntelliSense orchestrator.
// PRD: Slice "Ranking Engine". Pure module — no Monaco runtime dep, no
// classes, no I/O. The orchestrator slot in `range` (cursor word) and the
// optional `command` (signature-help trigger) at the call site where it has
// the live `monaco` instance in hand.
//
// Algorithm (RESEARCH.md §5.2):
//   Score = lexicographic tuple `(tier, matchQuality, leader, alreadyTyped,
//   acceptOrder, orderHint, name_asc)` — see `compareScored`.
//   Tier 1..8 with CTE-shadowing promotion to tier 1 when a CTE candidate
//   has a same-name table sibling.
//   Match quality: exact-prefix-same-case (0) > exact-prefix-case-folded (1)
//     > CamelCase initials (2) > word-boundary infix (3) > contiguous infix
//     (4) > subsequence fuzzy (5).
//   Filtering: drop NO_MATCH (6); drop subsequence fuzzy below the typed-
//     length threshold; drop "worse than CamelCase" when a quality≤2 result
//     exists.
//   Anti-SOUNDEX alias-dot filter: when `context.dottedBase` resolves to a
//     bound alias or CTE, keep only column candidates.
//   Preselect: exact case-insensitive match for the typed string regardless
//     of tier (RESEARCH.md §5.2 invariant).

import type {
  AliasBinding,
  CompletionCandidate,
  CompletionCandidateKind,
  CompletionCandidateTier,
  CompletionContext,
  CteBinding,
} from '@/core/engines';

import {
  FUZZY,
  matchQualityFor,
  NO_MATCH,
  type MatchQuality,
} from './matchQuality';
import { applyKeywordCasing, type KeywordCasingHint } from './keywordCasing';
import {
  clampTier,
  decoratedBadge,
  decoratedDemote,
  decoratedDetailSuffix,
  isDeprecated,
  mergeDetail,
} from './versionDecoration';
import { applyTierPromotions } from './tierPromotions';
import { collectAlreadyTypedNames, isAlreadyTyped } from './alreadyTyped';
import {
  acceptanceKey,
  getAcceptanceHistory,
  type AcceptanceRecord,
  type AcceptanceScope,
} from './acceptanceHistory';
import { caretBindingRelation, isCaretBindingLeader } from './leader';

/**
 * The position-invariant Monaco completion-item shape produced by the
 * ranker. The orchestrator wraps this into `monaco.languages.CompletionItem`
 * by translating `kind` (string) → numeric Monaco enum, slotting in `range`
 * (from the cursor word boundaries), and turning `triggerSignatureHelp` into
 * the `editor.action.triggerParameterHints` command.
 *
 * `labelDescription` (when set) becomes Monaco's `{ label, description }`
 * label-object form — used by the version-gating decorator to render
 * right-aligned badges like `≥ 2017`. `tags` (when set) becomes Monaco's
 * `tags: [CompletionItemTag.Deprecated]` — used to render the strikethrough
 * label for deprecated entries.
 */
export type MonacoCompletionItem = {
  readonly label: string;
  readonly labelDescription?: string;
  readonly kind: CompletionCandidateKind;
  readonly detail?: string;
  readonly documentation?: string;
  readonly insertText: string;
  readonly isSnippet: boolean;
  readonly sortText: string;
  readonly filterText: string;
  readonly preselect: boolean;
  readonly triggerSignatureHelp: boolean;
  /** Monaco `CompletionItemTag` numeric values; `1` = `Deprecated`. */
  readonly tags?: readonly number[];
};

const FUZZY_MIN_TYPED_LENGTH = 4;
const SORT_INDEX_PAD_WIDTH = 5;

/**
 * D7 (plan16 T6): cap on the number of ranked suggestions handed to Monaco
 * when the user hasn't typed anything yet (`typed === ''`). A bare
 * `SELECT * FROM <caret>` against a schema with thousands of tables would
 * otherwise forward every one of them to Monaco's completion widget every
 * keystroke. Applied AFTER ranking (tier/matchQuality/orderHint/name sort)
 * so the cap always keeps the top-N by quality, never an arbitrary subset.
 * Never applied when `typed.length > 0` — match-quality filtering already
 * narrows prefix-typed queries, and capping a typed query risks hiding an
 * exact match that happens to sort late.
 *
 * Bounded-population exemption (Plan 15 Gate D — mysql script.58 `WEEK`,
 * oracle script.11 §4 `NEXTVAL`/`CURRVAL`, oracle script.24 §2 data-dictionary
 * views): candidates from BOUNDED populations that rank past the cap are
 * RETAINED (appended in rank order). The cap's target pathology is the
 * unbounded schema-derived flood; a bounded candidate can never BE that
 * flood, and slicing bounded populations deterministically cut their
 * alphabetical tails whenever a real-sized schema pushed the pre-cap total
 * over 500. Two bounded shapes exist:
 *   - bounded KINDS ({@link CAP_EXEMPT_BOUNDED_KINDS}) — population bounded
 *     by the engine vocabulary or by the user's own statement;
 *   - bounded MEMBERS of unbounded kinds — engine-curated names sharing a
 *     schema-derived kind, flagged `capExempt` by their producer (oracle
 *     data-dictionary public synonyms among thousands of `kind: 'table'`
 *     synonyms, sqlite system tables, mysql system databases).
 * Worst case after the exemption is `cap + the bounded populations` (≈ 1k),
 * never thousands.
 */
export const UNTYPED_SUGGESTION_CAP = 500;

/**
 * Candidate kinds whose ENTIRE population is bounded — exempt from the
 * untyped cap (see {@link UNTYPED_SUGGESTION_CAP}). Two bounded sources:
 *   - engine-curated vocabulary: `keyword` (~150–230 names per engine),
 *     `system-function` (~65–240 per engine, incl. sequence pseudo-columns);
 *   - the user's own statement/script (scales with what the user typed,
 *     never with the schema): `snippet` (FK / loose-rel join templates for
 *     the statement's alias bindings), `variable` / `parameter` (declared
 *     locals + routine params in scope), `cte`, `alias`.
 * Unbounded schema-derived kinds (column/table/view/schema/database/routine/
 * system-object) stay capped; their curated members opt out individually via
 * `CompletionCandidate.capExempt`.
 */
const CAP_EXEMPT_BOUNDED_KINDS: ReadonlySet<CompletionCandidateKind> = new Set([
  'keyword',
  'system-function',
  'snippet',
  'variable',
  'parameter',
  'cte',
  'alias',
]);

type ScoredCandidate = {
  readonly candidate: CompletionCandidate;
  readonly tier: CompletionCandidateTier;
  readonly matchQuality: MatchQuality;
  /** 0 = the obvious default at this position (see ./leader.ts), 1 = not. */
  readonly leader: 0 | 1;
  /** 1 = already written in the clause being completed (see ./alreadyTyped.ts), 0 = not. */
  readonly alreadyTyped: 0 | 1;
  /** Accepted before in this scope → sorts by recency (lower = more recent); never accepted → MAX. */
  readonly acceptOrder: number;
  readonly orderHint: number;
  readonly bareName: string;
};

export type RankOptions = {
  readonly keywordCasing?: KeywordCasingHint;
  /**
   * Statement text from its start to the caret. Drives the "already typed"
   * in-tier tie-breaker: a name the user has already written in the clause
   * / list being completed stays offered but no longer leads. Omitted →
   * the tie-breaker is inert (unit tests, secondary call sites).
   */
  readonly statementPrefix?: string;
  /**
   * Completions the user accepted earlier in this (connection, database)
   * scope — see ./acceptanceHistory.ts. Keyed by {@link acceptanceKey}.
   * Accepted items float above never-accepted siblings inside a tier, most
   * recent first. Omitted → inert.
   */
  readonly acceptance?: ReadonlyMap<string, AcceptanceRecord>;
  /** Overrides {@link UNTYPED_SUGGESTION_CAP} — test-only escape hatch. */
  readonly untypedCap?: number;
  /**
   * Per-request sink for {@link RankReport}. Every ranking call made with
   * these options records into it, so a resolver that ranks on several
   * paths needs no per-return plumbing — the orchestrator owns the sink.
   */
  readonly report?: RankReport;
};

/**
 * What the orchestrator needs to know about a request's ranking (1.5.1 F3).
 * `wasTruncated` turns true (and stays true) when the untyped cap REMOVED at
 * least one candidate. A list can be longer than the cap with nothing
 * removed — bounded populations are kept past it — so the list length is
 * not a truncation signal; this flag is. It drives the orchestrator's
 * `incomplete`, i.e. whether Monaco re-queries on the next typed character.
 */
export type RankReport = { wasTruncated: boolean };

/**
 * The per-request {@link RankOptions} every resolver hands to
 * {@link rankCandidates}: the statement text before the caret feeds the
 * "already typed" tie-breaker. One helper so all five dialects pass the
 * same signals — ranking policy is cross-engine by contract.
 */
export function buildRankOptions(
  text: string,
  context: CompletionContext,
  offset: number,
  scope: AcceptanceScope & { readonly rankReport?: RankReport } = {},
): RankOptions {
  return {
    statementPrefix: text.slice(context.statementRange.start, offset),
    acceptance: getAcceptanceHistory(scope),
    ...(scope.rankReport ? { report: scope.rankReport } : {}),
  };
}

export function rankCandidates(
  candidates: readonly CompletionCandidate[],
  context: CompletionContext,
  typed: string,
  options: RankOptions = {},
): MonacoCompletionItem[] {
  const aliasFiltered = filterByAliasDot(candidates, context);
  const alreadyTyped = collectAlreadyTypedNames(options.statementPrefix ?? '');
  const caretRelation = caretBindingRelation(context);
  const acceptance = options.acceptance;

  const scored: ScoredCandidate[] = aliasFiltered.map((c) => {
    const bareName = extractBareName(c.label);
    return {
      candidate: c,
      tier: applyTierPromotions(c, aliasFiltered, context),
      matchQuality: matchQualityFor(c.label, typed),
      leader: isCaretBindingLeader(c, bareName, caretRelation) ? 0 : 1,
      alreadyTyped: isAlreadyTyped(c, bareName.toLowerCase(), alreadyTyped) ? 1 : 0,
      acceptOrder: acceptOrderFor(c, bareName, acceptance),
      orderHint: c.orderHint ?? Number.MAX_SAFE_INTEGER,
      bareName,
    };
  });

  const beforeFallback = scored.filter((s) => s.matchQuality !== NO_MATCH);
  const hasGoodMatches = beforeFallback.some((s) => s.matchQuality <= 2);
  const fuzzyAllowed = typed.length >= FUZZY_MIN_TYPED_LENGTH;
  const filtered = beforeFallback.filter((s) => {
    if (s.matchQuality === FUZZY && !fuzzyAllowed) return false;
    if (s.matchQuality > 2 && hasGoodMatches) return false;
    return true;
  });

  filtered.sort(compareScored);

  const untypedCap = options.untypedCap ?? UNTYPED_SUGGESTION_CAP;
  const capped =
    typed.length === 0 && filtered.length > untypedCap
      ? capUntypedKeepingBounded(filtered, untypedCap)
      : filtered;
  if (options.report && capped.length < filtered.length) options.report.wasTruncated = true;

  return capped.map((s, idx) => buildMonacoItem(s, idx, typed, options));
}

/** Apply the untyped cap while retaining bounded-population candidates that
 *  rank past it, in rank order (see {@link UNTYPED_SUGGESTION_CAP}). `ranked`
 *  must already be sorted by {@link compareScored}. */
function capUntypedKeepingBounded(
  ranked: readonly ScoredCandidate[],
  cap: number,
): ScoredCandidate[] {
  const kept = ranked.slice(0, cap);
  for (let i = cap; i < ranked.length; i++) {
    const s = ranked[i]!;
    if (CAP_EXEMPT_BOUNDED_KINDS.has(s.candidate.kind) || s.candidate.capExempt === true) {
      kept.push(s);
    }
  }
  return kept;
}

/** Recency rank of a previously accepted completion (lower = more recent);
 *  MAX when never accepted. Snippets match on `filterText` (the bare
 *  relation name for FK-join templates), everything else on the bare label. */
function acceptOrderFor(
  c: CompletionCandidate,
  bareName: string,
  acceptance: ReadonlyMap<string, AcceptanceRecord> | undefined,
): number {
  if (!acceptance || acceptance.size === 0) return Number.MAX_SAFE_INTEGER;
  const key = acceptanceKey(c.isSnippet && c.filterText ? c.filterText : bareName);
  const rec = acceptance.get(key);
  return rec ? Number.MAX_SAFE_INTEGER - rec.lastAcceptedSeq : Number.MAX_SAFE_INTEGER;
}

/**
 * Lexicographic order inside the ranked list:
 *   tier → matchQuality → leader → alreadyTyped → acceptOrder → orderHint → name.
 * `acceptOrder` (level 2) floats what the user accepted before in this
 * connection/database scope, most recent first — a personal signal that
 * outranks curated `orderHint` popularity but never the structural tiers.
 * `leader` and `alreadyTyped` are the in-tier tie-breakers: without them a
 * tier full of equally good matches fell through to alphabetical order, so
 * the relation the caret is editing lost to `acact_*` siblings and a value
 * already written in the list still led it (coverage slugs
 * `rank-expected-leader-not-first`, `rank-already-typed-value-still-leads`).
 * Both sit BELOW matchQuality: what the user typed always wins.
 */
function compareScored(a: ScoredCandidate, b: ScoredCandidate): number {
  if (a.tier !== b.tier) return a.tier - b.tier;
  if (a.matchQuality !== b.matchQuality) return a.matchQuality - b.matchQuality;
  if (a.leader !== b.leader) return a.leader - b.leader;
  if (a.alreadyTyped !== b.alreadyTyped) return a.alreadyTyped - b.alreadyTyped;
  if (a.acceptOrder !== b.acceptOrder) return a.acceptOrder - b.acceptOrder;
  if (a.orderHint !== b.orderHint) return a.orderHint - b.orderHint;
  return a.bareName.localeCompare(b.bareName);
}

function buildMonacoItem(
  s: ScoredCandidate,
  sortIndex: number,
  typed: string,
  options: RankOptions,
): MonacoCompletionItem {
  const padded = sortIndex.toString().padStart(SORT_INDEX_PAD_WIDTH, '0');
  const effectiveTier = clampTier(s.tier + decoratedDemote(s.candidate));
  const sortText = `${effectiveTier}_${padded}`;
  const preselect = isExactCaseInsensitiveMatch(s.candidate.label, typed);

  const { label, insertText } = applyCasing(s.candidate, options.keywordCasing);
  const detailMerged = mergeDetail(s.candidate.detail, decoratedDetailSuffix(s.candidate));
  const badge = decoratedBadge(s.candidate);
  const tags = isDeprecated(s.candidate) ? ([1] as const) : undefined;

  const item: MonacoCompletionItem = {
    label,
    kind: s.candidate.kind,
    detail: detailMerged,
    documentation: s.candidate.documentation,
    insertText,
    isSnippet: s.candidate.isSnippet,
    sortText,
    filterText: s.candidate.filterText ?? s.bareName,
    preselect,
    triggerSignatureHelp: s.candidate.triggerSignatureHelp ?? false,
  };
  return {
    ...item,
    ...(badge ? { labelDescription: badge } : {}),
    ...(tags ? { tags } : {}),
  };
}

function applyCasing(
  candidate: CompletionCandidate,
  hint: KeywordCasingHint | undefined,
): { label: string; insertText: string } {
  if (!hint || candidate.kind !== 'keyword') {
    return { label: candidate.label, insertText: candidate.insertText };
  }
  return {
    label: applyKeywordCasing(candidate.label, hint),
    insertText: applyKeywordCasing(candidate.insertText, hint),
  };
}

function isExactCaseInsensitiveMatch(label: string, typed: string): boolean {
  if (typed.length === 0) return false;
  return label.toLowerCase() === typed.toLowerCase();
}

function filterByAliasDot(
  candidates: readonly CompletionCandidate[],
  context: CompletionContext,
): readonly CompletionCandidate[] {
  if (!context.dottedBase || context.dottedBase.parts.length === 0) {
    return candidates;
  }
  const baseLower = context.dottedBase.parts[0]!.toLowerCase();
  if (resolvesToAliasOrCte(baseLower, context.aliasBindings, context.cteBindings)) {
    return candidates.filter((c) => c.kind === 'column');
  }
  return candidates;
}

function resolvesToAliasOrCte(
  baseLower: string,
  aliasBindings: readonly AliasBinding[],
  cteBindings: readonly CteBinding[],
): boolean {
  if (aliasBindings.some((b) => b.alias.toLowerCase() === baseLower)) return true;
  if (cteBindings.some((b) => b.name.toLowerCase() === baseLower)) return true;
  return false;
}

function extractBareName(label: string): string {
  if (label.length < 2) return label;
  const first = label[0]!;
  const last = label[label.length - 1]!;
  if (
    (first === '[' && last === ']') ||
    (first === '"' && last === '"') ||
    (first === '`' && last === '`')
  ) {
    return label.slice(1, -1);
  }
  return label;
}
